import { Injectable } from '@angular/core';
import { capSQLiteChanges, capSQLiteValues } from '@capacitor-community/sqlite';
import { Logger } from '../../log/logger';
import { DatabaseService } from '../database.service';
import { IDbSet } from '../dbset.interface';
import { BoondockPhoto } from '../entities/boondock-photo';

const TABLE_NAME = 'boondock_photos';

@Injectable({
  providedIn: 'root',
})
export class BoondockPhotosDbSet implements IDbSet {
  constructor(private databaseService: DatabaseService, private logger: Logger) {}

  public store(request: BoondockPhoto): Promise<capSQLiteValues> {
    return this.databaseService.insert(TABLE_NAME, this.mapObjectsToStorable([request]).pop());
  }

  public storeArray(requests: BoondockPhoto[]): Promise<capSQLiteValues> {
    return this.databaseService.insertMulti(TABLE_NAME, this.mapObjectsToStorable(requests));
  }

  public async truncateAndStoreArray(records: BoondockPhoto[]): Promise<capSQLiteValues> {
    records = this.mapObjectsToStorable(records);
    await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
    const { statement, values } = this.databaseService.generateInsertStatement(TABLE_NAME, records);
    this.logger.debug('Executing INSERT STATEMENT', statement);
    return this.databaseService.query(statement, values);
  }

  public async truncate(): Promise<capSQLiteChanges> {
    return await this.databaseService.execute(`DELETE FROM ${TABLE_NAME};`);
  }

  public async retrieve(id: number): Promise<{ values: BoondockPhoto[] }> {
    const results = await this.databaseService.select(TABLE_NAME, { id });
    return {
      values: this.mapResultsObject(results),
    };
  }

  public async retrieveAll(limit?: number): Promise<BoondockPhoto[]> {
    const results = await this.databaseService.selectAll(TABLE_NAME, undefined, limit);
    return this.mapResultsObject(results);
  }

  public async retrieveForListingId(listingId: number): Promise<BoondockPhoto[]> {
    const results = await this.databaseService.select(TABLE_NAME, { listing_id: listingId });
    return this.mapResultsObject(results);
  }

  public async retrieveBatch(listingId: number[]): Promise<BoondockPhoto[]> {
    if (listingId.length > 0) {
      let statement = `SELECT * FROM ${TABLE_NAME} WHERE listing_id IN(`;
      const values: string[] = [];
      listingId.forEach((el) => {
        statement += '?, ';
        values.push(el.toString());
      });
      statement = statement.substr(0, statement.length - 2) + `);`;
      const results = await this.databaseService.query(statement, values);
      return this.mapResultsObject(results);
    } else {
      return [];
    }
  }

  public async remove(id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { id });
  }

  public async removeByListingId(id: string): Promise<capSQLiteValues> {
    return await this.databaseService.delete(TABLE_NAME, { listing_id: id });
  }

  private mapResultsObject(results: capSQLiteValues): BoondockPhoto[] {
    if (results.values) {
      return results.values?.map((record) => {
        return {
          id: record.id,
          boondockId: record.boondock_id,
          caption: record.caption,
          key: record.key,
        };
      });
    } else {
      return [];
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mapObjectsToStorable(items: BoondockPhoto[]): any[] {
    if (items) {
      return items?.map((record: BoondockPhoto) => {
        return {
          id: record.id,
          boondock_id: record.boondockId,
          caption: record.caption,
          key: record.key,
        };
      });
    } else {
      return [];
    }
  }
}
